import {
  type Actions,
  ImmerReducer,
  createActionCreators,
  createReducerFunction,
} from "immer-reducer";
import {
  anyPass,
  clone,
  findIndex,
  forEachObjIndexed,
  isNil,
  pathSatisfies,
  propEq,
  remove,
} from "ramda";
import type { DefaultRootState } from "react-redux";
import type { ThunkAction, ThunkDispatch } from "redux-thunk";
import type { ISetToolbarSnack } from "actions/context";

import { requestStatusSelector } from "group_management/selectors";
import {
  GroupManagementRequestKeys,
  type GroupManagementRequests,
  type IGroupManagementState,
  type IItemRecord,
  type IMenuComponents,
  type IMenuRecord,
  type ISection,
} from "group_management/types";
import { isRequestFailure, isRequestNotStarted } from "helpers/requests";
import { RequestStatus } from "types/request";

const EMPTY_REQUEST_STATUS: GroupManagementRequests = {
  [GroupManagementRequestKeys.GetMenus]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.GetSections]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.GetMenuComponents]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostItem]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PutItem]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostMenu]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PutMenu]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.DeleteMenu]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostOrPutItem]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostCategory]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PutCategory]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.DeleteCategory]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostOrPutCategory]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostCourse]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PutCourse]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostOrPutCourse]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostModifier]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PutModifier]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostOrPutModifier]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostModifierGroup]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PutModifierGroup]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostOrPutModifierGroup]:
    RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostSection]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PutSection]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.DeleteSection]: RequestStatus.NOT_STARTED,
  [GroupManagementRequestKeys.PostOrPutSection]: RequestStatus.NOT_STARTED,
};

// The per-location collections whose records the bulk save thunks upsert or
// remove one at a time.
export type GroupManagementComponentCollection =
  | "categories"
  | "courses"
  | "modifiers"
  | "modifierGroups"
  | "sections";

export class GroupManagementReducer extends ImmerReducer<IGroupManagementState> {
  public setRequestStatus(
    storeId: string,
    requestKey: GroupManagementRequestKeys,
    status: RequestStatus
  ) {
    if (storeId) {
      if (!this.draftState.requestStatus[storeId]) {
        this.draftState.requestStatus[storeId] = clone(EMPTY_REQUEST_STATUS);
      }
      this.draftState.requestStatus[storeId][requestKey] = status;
    }
  }

  public setRequestStatusForMultilocation(
    storeIds: string[],
    requestKey: GroupManagementRequestKeys,
    status: RequestStatus
  ) {
    storeIds.forEach((storeId) =>
      this.setRequestStatus(storeId, requestKey, status)
    );
  }

  public setMenusForRestaurant(storeId: string, menus: IMenuRecord[]) {
    if (storeId) {
      this.draftState.menus[storeId] = menus;
    }
  }

  public updateMenuForRestaurant(storeId: string, menu: IMenuRecord) {
    if (storeId) {
      const existingMenus = this.draftState.menus[storeId];
      if (existingMenus) {
        const existingIdx = existingMenus.findIndex(propEq("id", menu.id));
        if (existingIdx === -1) {
          existingMenus.push(menu);
        } else {
          existingMenus.splice(existingIdx, 1, menu);
        }
      } else {
        this.draftState.menus[storeId] = [menu];
      }
    }
  }

  public deleteMenuForRestaurant(storeId: string, menu: IMenuRecord) {
    const existingMenus = this.draftState.menus[storeId];
    if (existingMenus) {
      const existingIdx = existingMenus.findIndex(propEq("id", menu.id));
      this.draftState.menus[storeId] = remove(existingIdx, 1, existingMenus);
    }
  }

  public setSectionsForRestaurant(storeId: string, sections: ISection[]) {
    if (storeId) {
      this.draftState.sections[storeId] = sections;
    }
  }

  public setMenuComponentsForRestaurant(
    storeId: string,
    menuComponents: IMenuComponents
  ) {
    if (storeId) {
      forEachObjIndexed((list, key) => {
        // Optional payload keys (modifiers, on older summaries) are skipped;
        // keys with no matching state slice are ignored as before.
        if (this.draftState[key] && list) {
          this.draftState[key][storeId] = list;
        }
      }, menuComponents);
    }
  }

  public setMenuComponentsForMultiLocation(
    keyedComponents: Record<string, IMenuComponents>
  ) {
    forEachObjIndexed(
      (menuComponents, storeId) =>
        this.setMenuComponentsForRestaurant(storeId, menuComponents),
      keyedComponents
    );
  }

  public saveItem(storeId: string, item: IItemRecord) {
    const storeItems = this.draftState.items[storeId];
    if (storeItems) {
      const itemIdx = findIndex(propEq("id", item.id), storeItems);
      if (itemIdx === -1) {
        storeItems.push(item);
      } else {
        storeItems.splice(itemIdx, 1, item);
      }
    } else {
      this.draftState.items[storeId] = [item];
    }
  }

  // Generic upsert for the entity collections the bulk save thunks write
  // (same find-or-push body as saveItem). Category "deletes" land here too:
  // archiving returns the record with an archived status, so it upserts.
  public saveComponentRecord(
    storeId: string,
    collection: GroupManagementComponentCollection,
    record: { id: string }
  ) {
    const storeRecords = this.draftState[collection][storeId] as {
      id: string;
    }[];
    if (storeRecords) {
      const recordIdx = findIndex(propEq("id", record.id), storeRecords);
      if (recordIdx === -1) {
        storeRecords.push(record);
      } else {
        storeRecords.splice(recordIdx, 1, record);
      }
    } else {
      this.draftState[collection][storeId] = [record as any];
    }
  }

  // Removal for hard deletes (display groups). Menus keep their own
  // deleteMenuForRestaurant above.
  public removeComponentRecord(
    storeId: string,
    collection: GroupManagementComponentCollection,
    recordId: string
  ) {
    const storeRecords = this.draftState[collection][storeId] as {
      id: string;
    }[];
    if (storeRecords) {
      const recordIdx = findIndex(propEq("id", recordId), storeRecords);
      if (recordIdx !== -1) {
        storeRecords.splice(recordIdx, 1);
      }
    }
  }
}

const initialState: IGroupManagementState = {
  menus: {},
  sections: {},
  items: {},
  categories: {},
  courses: {},
  // Must exist before the summary's modifiers key can land:
  // setMenuComponentsForRestaurant only writes payload keys already present
  // on the draft state.
  modifiers: {},
  modifierGroups: {},
  printerGroups: {},
  taxRates: {},
  requestStatus: {},
};

export default createReducerFunction(GroupManagementReducer, initialState);

export const ActionCreators = createActionCreators(GroupManagementReducer);
export type GroupManagementActions =
  | Actions<typeof GroupManagementReducer>
  | ISetToolbarSnack;

export type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  GroupManagementActions
>;

// A thunk-aware dispatch: `useDispatch<AppThunkDispatch>()` returns the
// resolved value of a dispatched ThunkResult (the middleware unwraps it),
// so callers don't need to cast the dispatch or its result.
export type AppThunkDispatch = ThunkDispatch<
  DefaultRootState,
  undefined,
  GroupManagementActions
>;

export const isFetchNeededForLocation = (
  storeId: string,
  requestKey: GroupManagementRequestKeys,
  getState: () => DefaultRootState
) =>
  pathSatisfies(
    anyPass([isNil, isRequestNotStarted, isRequestFailure]),
    [storeId, requestKey],
    requestStatusSelector(getState())
  );
